import {
  AfterContentInit,
  Component,
  contentChildren,
  effect,
  ElementRef,
  HostBinding,
  input,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatColumnDef, MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import { SkeletonLoaderComponent } from '@shared/components/skeleton-loader/skeleton-loader.component';
import { ISkeletonColumn } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { get } from 'lodash';
import { Subscription } from 'rxjs';

@Component({
  imports: [MatTableModule, MatSortModule, SkeletonLoaderComponent, TranslateModule],
  selector: 'app-analytics-details-table',
  styleUrl: './analytics-details-table.component.scss',
  templateUrl: './analytics-details-table.component.html',
})
export class AnalyticsDetailsTableComponent<T> implements AfterContentInit, OnDestroy {
  @HostBinding('class') public readonly hostClass: string = 'block';

  public readonly displayedColumns = input.required<string[]>();
  public readonly dataSource = input.required<MatTableDataSource<T>>();

  public readonly dataCount = input<number | null>(null);
  public readonly selectedRowId = input<string | null>(null);
  public readonly sort = input<MatSort>();

  public readonly loading = signal<boolean>(true);

  public readonly columnDefs = contentChildren(MatColumnDef);

  public readonly table = viewChild(MatTable);
  public readonly tableContainer = viewChild<ElementRef<HTMLElement>>('tableContainer');

  public readonly skeletonLoaderTableColumns = signal<ISkeletonColumn[]>([]);

  private dataSourceSubscription?: Subscription;

  constructor() {
    effect(() => {
      this.setSkeletonLoaderTableColumns();
    });

    effect(() => {
      const selectedId: string | null = this.selectedRowId();
      const containerElement: HTMLElement | undefined = this.tableContainer()?.nativeElement;

      if (selectedId && containerElement) {
        this.performInTableScroll(containerElement, selectedId);
      }
    });

    effect(() => {
      if (this.dataCount() === null) {
        return;
      }

      setTimeout(() => this.checkIfTableRendered());
    });
  }

  public ngAfterContentInit(): void {
    this.updateTableColumns();
    this.dataSource().sort = this.sort() ?? null;
    this.dataSource().sortingDataAccessor = this.getSortingValue.bind(this);
  }

  public setSkeletonLoaderTableColumns(): void {
    const columns = this.displayedColumns();
    const result: ISkeletonColumn[] = [];

    for (let i = 0; i < columns.length; i++) {
      if (i === 0 || columns[i] === 'timestamp' || columns[i] === 'videoTime') {
        result.push({
          className: 'w-[120px]',
          columnPadding: '12px 28px 12px 16px',
          height: '20px',
          rowMargin: '0 0 2px 0',
          rowPadding: '4px 0',
          textWidth: '48px',
          type: 'text',
          width: columns.length === 1 ? '100%' : '60px',
        });
      } else if (i === columns.length - 1) {
        result.push({
          borderRadius: '100px',
          circleSize: '24px',
          columnPadding: '12px 16px',
          height: '24px',
          rowMargin: '0 0 2px 0',
          rowPadding: '0',
          type: 'icon',
          width: '0',
        });
      } else {
        result.push({
          className: 'flex-1',
          columnPadding: '12px 16px',
          height: '20px',
          rowMargin: '0 0 2px 0',
          rowPadding: '0',
          type: 'text',
        });
      }
    }

    this.skeletonLoaderTableColumns.set(result);
  }

  public ngOnDestroy(): void {
    this.dataSourceSubscription?.unsubscribe();
  }

  private performInTableScroll(containerElement: HTMLElement, selectedId: string): void {
    const rowElement: HTMLElement | null = containerElement.querySelector(`#row-${selectedId}`);

    if (!rowElement) {
      return;
    }

    const tdElements: NodeListOf<HTMLTableCellElement> = rowElement.querySelectorAll('td');

    tdElements.forEach((td: HTMLTableCellElement) => {
      td.classList.add('!bg-shikenso-pink-base-24');
    });

    setTimeout(() => {
      tdElements.forEach((td: HTMLTableCellElement) => {
        td.classList.remove('!bg-shikenso-pink-base-24');
      });
    }, 1000);

    rowElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }

  private getSortingValue(item: unknown, property: string): number | string {
    if (property === 'videoTime') {
      const videoTimeSeconds = get(item, 'videoTimeSeconds');

      if (typeof videoTimeSeconds === 'number') {
        return videoTimeSeconds;
      }

      const fallbackVideoTimeValue: unknown = get(item, 'videoTimeLabel') ?? get(item, property);

      if (typeof fallbackVideoTimeValue === 'string') {
        const trimmedVideoTimeValue = fallbackVideoTimeValue.trim();

        if (trimmedVideoTimeValue) {
          return DurationUtilitiesService.convertVideoTimeToSeconds(trimmedVideoTimeValue);
        }
      }
    }

    return (get(item, property) as string) ?? '';
  }

  private updateTableColumns(): void {
    const columnDefs = this.columnDefs();
    const table = this.table();

    if (!columnDefs || !table) {
      return;
    }

    columnDefs.forEach((columnDef: MatColumnDef) => {
      table.addColumnDef(columnDef);
    });
  }

  private checkIfTableRendered(): void {
    const tableElement = this.tableContainer()?.nativeElement;
    const expectedRowCount = this.dataCount();

    if (!tableElement || expectedRowCount === null) {
      return;
    }

    const renderedRows = tableElement.querySelectorAll('tr.data-row');

    if (expectedRowCount > 0 && renderedRows.length === 0) {
      return;
    }

    if (renderedRows.length === expectedRowCount) {
      this.loading.set(false);
    }
  }
}
